import esriConfig from '@arcgis/core/config.js';
import esriRequest from '@arcgis/core/request.js';
import IdentityManager from '@arcgis/core/identity/IdentityManager.js';
import ServerInfo from '@arcgis/core/identity/ServerInfo.js';
import type { PortalInfo, TokenInfo } from '../../../shared/ipc';
import { originWithBase } from '../platform/base-url';

const RETRIED_FLAG = '__rcmcTokenRetried';

type RetriedRequestOptions = __esri.RequestOptions & {
  query?: Record<string, unknown>;
  [RETRIED_FLAG]?: boolean;
};

type EsriErrorLike = Error & {
  details?: {
    httpStatus?: number;
    messageCode?: string | number;
    subCode?: number;
    url?: string;
    requestOptions?: RetriedRequestOptions;
  };
};

function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function attachToken(opts: RetriedRequestOptions, token: string): void {
  opts.query = { ...(opts.query ?? {}), token };
}

// In browser mode the renderer can't reach a self-signed Enterprise portal
// directly (cert + CORS). Rewrite portal URLs to /portal/* so they go through
// the dev server proxy. AGOL doesn't need this — services*.arcgis.com supports
// CORS and the SDK can hit it directly. Electron has its own session hooks.
function makePortalRewriter(host: string): ((url: string) => string) | null {
  if (typeof window === 'undefined' || window.__platform__ !== 'web') return null;
  const proxyBase = `${originWithBase()}/portal`;
  return (url: string) => {
    if (!url) return url;
    const lower = url.toLowerCase();
    const httpsPrefix = `https://${host.toLowerCase()}`;
    const httpPrefix = `http://${host.toLowerCase()}`;
    if (lower.startsWith(httpsPrefix)) return proxyBase + url.slice(httpsPrefix.length);
    if (lower.startsWith(httpPrefix)) return proxyBase + url.slice(httpPrefix.length);
    return url;
  };
}

function isTokenError(err: EsriErrorLike): boolean {
  const d = err?.details ?? {};
  const candidates: Array<unknown> = [d.httpStatus, d.messageCode, d.subCode];
  return candidates.some((c) => c === 498 || c === 499 || c === '498' || c === '499');
}

function registerToken(info: TokenInfo, userId: string): void {
  // Tells the SDK to attach this token to every request that ServerInfo
  // resolves to the same server. For AGOL (server = www.arcgis.com/sharing/rest)
  // this includes the entire *.arcgis.com fleet, which is the whole reason we
  // need this — services3.arcgis.com would otherwise prompt for credentials.
  IdentityManager.registerToken({
    server: info.tokenServer,
    token: info.token,
    expires: info.expiresAt,
    ssl: info.ssl,
    userId,
  });
}

// Client-side token cache. Without this, the `before` interceptor below would
// call window.api.getArcgisToken() on every SDK request — a roundtrip per tile
// during zoom/pan. The server already caches by referer, so upstream portal
// calls are bounded, but the per-request HTTP/IPC churn still adds up. Mirror
// the server's TTL + inflight coalescing here so a fresh token is only fetched
// when the cached one is actually about to expire (or on a 498/499 retry).
const TOKEN_REFRESH_MARGIN_MS = 60_000;
let cachedToken: TokenInfo | null = null;
let inflightToken: Promise<TokenInfo> | null = null;

export async function getCachedToken(force = false): Promise<TokenInfo> {
  const now = Date.now();
  if (!force && cachedToken && cachedToken.expiresAt - TOKEN_REFRESH_MARGIN_MS > now) {
    return cachedToken;
  }
  if (!force && inflightToken) return inflightToken;
  const p = (window.api.getArcgisToken(force) as Promise<TokenInfo>)
    .then((info) => {
      cachedToken = info;
      return info;
    })
    .finally(() => {
      if (inflightToken === p) inflightToken = null;
    });
  inflightToken = p;
  return p;
}

let initPromise: Promise<void> | null = null;

export function initArcgisRequest(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const portal = (await window.api.getArcgisPortalInfo()) as PortalInfo;
    const initialToken = await getCachedToken();

    // Pre-seed IdentityManager so the SDK never falls into its sign-in flow.
    if (portal.kind === 'agol') {
      // Make sure the SDK knows www.arcgis.com is a token server. Without this
      // it usually figures it out, but registering ServerInfo explicitly avoids
      // any surprise around hosted feature service URLs in obscure regions.
      IdentityManager.registerServers([
        new ServerInfo({
          server: portal.tokenServer,
          tokenServiceUrl: `${portal.tokenServer}/generateToken`,
          hasPortal: true,
          hasServer: true,
        }),
      ]);
    }
    registerToken(initialToken, await getUsername());

    const hostPattern = new RegExp(`^https?://${escapeRegex(portal.host)}(:\\d+)?/`, 'i');
    const rewrite = portal.kind === 'enterprise' ? makePortalRewriter(portal.host) : null;
    const proxyOriginPattern = rewrite
      ? new RegExp(`^${escapeRegex(originWithBase())}/portal/`, 'i')
      : null;

    // For AGOL, also catch hosted-service domains so a stale token gets
    // refreshed via the error interceptor below even if IdentityManager misses
    // a 498 (it won't always retry without a credential challenge handler).
    const agolServicePattern = portal.kind === 'agol'
      ? /^https?:\/\/[^/]*\.arcgis\.com\//i
      : null;

    const matchUrls: RegExp[] = [hostPattern];
    if (proxyOriginPattern) matchUrls.push(proxyOriginPattern);
    if (agolServicePattern) matchUrls.push(agolServicePattern);

    const interceptors = (esriConfig.request.interceptors ??= []);

    const interceptor: __esri.RequestInterceptor = {
      urls: matchUrls,
      before: async (params: __esri.BeforeInterceptorCallbackParams) => {
        const opts = (params.requestOptions ?? {}) as RetriedRequestOptions;
        params.requestOptions = opts;
        if (rewrite) params.url = rewrite(params.url);
        // IdentityManager normally injects the token already; this is a
        // belt-and-suspenders for the rewritten /portal/* URL (whose host no
        // longer matches any registered ServerInfo) and AGOL-direct calls.
        if (opts[RETRIED_FLAG]) return;
        const info = await getCachedToken();
        attachToken(opts, info.token);
      },
      error: async (rawError: Error) => {
        const err = rawError as EsriErrorLike;
        if (!isTokenError(err)) return;
        const rawUrl = err.details?.url;
        const opts = err.details?.requestOptions;
        if (!rawUrl || !opts || opts[RETRIED_FLAG]) return;
        const url = rewrite ? rewrite(rawUrl) : rawUrl;
        const fresh = await getCachedToken(true);
        registerToken(fresh, await getUsername());
        attachToken(opts, fresh.token);
        opts[RETRIED_FLAG] = true;
        return await esriRequest(url, opts);
      },
    };

    interceptors.push(interceptor);
  })();
  return initPromise;
}

// We don't expose the username over the Api yet; the IdentityManager just needs
// *some* userId string, and a stable placeholder keeps registerToken idempotent
// (re-registering with the same userId replaces the credential rather than
// stacking up).
async function getUsername(): Promise<string> {
  return 'rcmc-app';
}
